import { Box, BoxProps, Center, Flex, IconButton, Image, Portal, useToken } from "@chakra-ui/react";
import { MdOutlineEdit } from "react-icons/md";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router";

import {
  CatalogueIcon,
  DashboardIcon,
  LeftArrowIcon,
  LiveDisabledIcon,
  LiveIcon,
  ManagementIcon,
  ProjectIcon,
  RanaLogoSmall,
  RightArrowIcon,
  SIDEBAR_CLOSED_PX,
  SIDEBAR_OPEN_PX,
  SIDEBAR_PADDING_PX,
  SIDEBAR_TRANSITION,
  SettingsIcon,
} from "@nens/ranalib";

import { ColorModeButton } from "@/components/color-mode-button";
import { SidebarButton } from "@/components/sidebar-button";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { useRanaDroplets } from "@/hooks/use-rana-droplets";
import dropsWiseIconSrc from "@/images/dropswise-icon.svg?url";
import dropsWiseLogoSrc from "@/images/dropswise-logo.svg?url";
import { ScreenRecordButton } from "@/lib/rana-screenrecord-button";
import { useHccLiveSiteAccessQuery } from "@/queries/query-hcc-live-site-access";

import { EmptyQueryState } from "./components/empty-query-state";
import { SidebarProfilePopup } from "./components/sidebar-profile-popup";
import { useSidebar } from "./hooks/use-sidebar";

interface SidebarProps {
  activeButton?: string;
}

export function Sidebar({ activeButton }: SidebarProps) {
  const intl = useIntl();
  const { isOpen, openSidebar, closeSidebar, tenantBaseUrl, canOpenAdminPanel, isDropsWise } =
    useSidebar();

  const { canReadUserList, canManageTenant } = useTenantActionAllowed();

  const canOpenTenantSettings = canReadUserList || canManageTenant;

  // Check that user has access to the live site
  const liveSiteAccessQuery = useHccLiveSiteAccessQuery();
  const liveSiteDisabled = !liveSiteAccessQuery.hasAccess;
  const liveSiteDisabledTooltip = intl.formatMessage({
    id: "sidebar.live.disabled",
    defaultMessage:
      "Rana Live is unavailable because your organisation does not have a Rana Live subscription.",
  });

  const sidebarBgToken = isDropsWise ? "drops_wise.bg" : "brand.clear_creek_bg";

  const sizeProps: BoxProps = {
    w: isOpen ? `${SIDEBAR_OPEN_PX}px` : `${SIDEBAR_CLOSED_PX}px`,
    h: "vh",
    transition: SIDEBAR_TRANSITION,
  };

  return (
    <Box {...sizeProps}>
      {/* Portal so it does not get animated by transition in tenant-routes.tsx */}
      <Portal>
        <Flex
          {...sizeProps}
          position="absolute"
          top={0}
          left={0}
          flexShrink={0}
          direction="column"
          justifyContent="space-between"
          color="white"
          bg={sidebarBgToken}
          zIndex="sidebar"
        >
          <Box position="relative">
            <Box
              position="absolute"
              pointerEvents="none"
              top="0"
              overflow="hidden"
              w={isOpen ? `${SIDEBAR_OPEN_PX}px` : `${SIDEBAR_CLOSED_PX}px`}
              transition={SIDEBAR_TRANSITION}
              opacity={isOpen ? 0.5 : 0}
            >
              <SidebarDroplets backgroundColorToken={sidebarBgToken} />
            </Box>
            <Flex
              as={RouterLink}
              to={tenantBaseUrl}
              position="relative"
              zIndex="docked"
              h={6}
              mt={4}
              gap={1}
              alignItems="center"
              textStyle="lg"
              fontWeight={300}
              px={`${SIDEBAR_PADDING_PX}px`}
              cursor="pointer"
            >
              {isDropsWise ? <DropsWiseLogo isOpen={isOpen} /> : <RanaLogo isOpen={isOpen} />}
            </Flex>
          </Box>
          <Flex
            position="absolute"
            top="1/2"
            transform="translateY(-50%)"
            flexDirection="column"
            gap={4}
            p={`${SIDEBAR_PADDING_PX}px`}
          >
            <SidebarButton
              icon={DashboardIcon}
              text={intl.formatMessage({ id: "sidebar.dashboard", defaultMessage: "Dashboard" })}
              isActive={activeButton === "dashboard"}
              isOpen={isOpen}
              to={tenantBaseUrl}
            />
            <SidebarButton
              icon={ProjectIcon}
              text={intl.formatMessage({ id: "sidebar.projects", defaultMessage: "Projects" })}
              isActive={activeButton === "projects"}
              isOpen={isOpen}
              to={`${tenantBaseUrl}/projects`}
            />
            <SidebarButton
              icon={CatalogueIcon}
              text={intl.formatMessage({
                id: "sidebar.catalogue",
                defaultMessage: "Catalogue",
              })}
              isActive={activeButton === "catalogue"}
              isOpen={isOpen}
              to={`${tenantBaseUrl}/catalogue`}
            />
            {canOpenTenantSettings && (
              <SidebarButton
                icon={SettingsIcon}
                text={intl.formatMessage({ id: "sidebar.settings", defaultMessage: "Settings" })}
                isActive={activeButton === "settings"}
                isOpen={isOpen}
                to={`${tenantBaseUrl}/settings`}
              />
            )}
            {canOpenAdminPanel && (
              <SidebarButton
                icon={MdOutlineEdit}
                iconShrinkBy={6}
                text={intl.formatMessage({ id: "sidebar.admin", defaultMessage: "Admin" })}
                isActive={activeButton === "admin"}
                isOpen={isOpen}
                to={`${tenantBaseUrl}/admin/detail`}
              />
            )}
            <SidebarButton
              icon={liveSiteDisabled ? LiveDisabledIcon : LiveIcon}
              iconShrinkBy={4}
              text={intl.formatMessage({ id: "sidebar.live", defaultMessage: "Rana Live" })}
              isActive={activeButton === "live"}
              isOpen={isOpen}
              isDisabled={liveSiteDisabled}
              disabledTooltip={liveSiteDisabledTooltip}
              href="/live"
            />
            <SidebarButton
              icon={ManagementIcon}
              iconShrinkBy={6}
              text={intl.formatMessage({ id: "sidebar.management", defaultMessage: "Rana HCC" })}
              isActive={activeButton === "management"}
              isOpen={isOpen}
              href="/hcc-management"
            />
          </Flex>
          <Box mb={2}>
            <Center gap={2} flexDir="column" mb={3} w={`${SIDEBAR_CLOSED_PX}px`}>
              <ScreenRecordButton variant="fourthiary" color="white" _hover={{ color: "white" }} />
              <ColorModeButton />
              <EmptyQueryState />
            </Center>

            <SidebarProfilePopup sidebarIsOpen={isOpen} />
          </Box>
          <IconButton
            aria-label="expand"
            icon={isOpen ? <LeftArrowIcon boxSize={4} /> : <RightArrowIcon boxSize={4} />}
            onClick={() => (isOpen ? closeSidebar() : openSidebar())}
            rounded="full"
            size="sm"
            justifyContent="center"
            alignItems="center"
            borderWidth="thick"
            borderColor="brand.grey.200"
            color="brand.keep_analytic"
            bgColor="white"
            _hover={{ bgColor: "brand.grey.100" }}
            position="absolute"
            left={`calc(${isOpen ? SIDEBAR_OPEN_PX : SIDEBAR_CLOSED_PX}px - 16px)`}
            top={24}
            zIndex="sidebar"
            transition="left 300ms ease-in"
          />
        </Flex>
      </Portal>
    </Box>
  );
}

const DropsWiseLogo = ({ isOpen }: { isOpen: boolean }) =>
  isOpen ? (
    <Image src={dropsWiseLogoSrc} alt="DropsWise" h={8} margin="auto" />
  ) : (
    <Image src={dropsWiseIconSrc} alt="DropsWise" h={6} mb={1} margin="auto" />
  );

const RanaLogo = ({ isOpen }: { isOpen: boolean }) => (
  <>
    <RanaLogoSmall width={`${SIDEBAR_CLOSED_PX - SIDEBAR_PADDING_PX * 2}px`} mb={1} />
    {isOpen && <span>rana</span>}
  </>
);

const SidebarDroplets = ({ backgroundColorToken }: { backgroundColorToken: string }) => {
  // Needs to be in seperate component because Portal messes up React life cycle timing of the droplets
  const [clear_creek, analytic] = useToken("colors", [backgroundColorToken, "brand.analytic"]);

  const dropletsRef = useRanaDroplets({
    squareSize: 20,
    backgroundColor: clear_creek,
    dropletColor: analytic,
    radiusFunction: (x, y) => Math.max(1 - Math.sqrt((x / 15) ** 2 + (y / 12) ** 2), 0),
    alignX: "right",
    alignY: "bottom",
    offsetX: -4,
    offsetY: -10,
  });

  return <Box ref={dropletsRef} w={`${SIDEBAR_OPEN_PX}px`} h={`${SIDEBAR_OPEN_PX}px`} />;
};
